<template>
    <div class="view-creative-carousel modal-content" :class="{ 'modal-content-bundle': isBundle }">
        <div class="relative">
            <template v-if="currentCreative">
                <!-- Header -->
                <div class="modal-header">
                    <h5 class="modal-title">View Creative</h5>
                    <div class="modal-buttons">
                        <download-order-items
                            download-type="creative"
                            :query="{ filter: { id: creativeIds } }"
                        />
                        <share-order-items
                            share-type="creative"
                            :query="{ filter: { id: creativeIds } }"
                        />
                        <button
                            type="button"
                            class="btn-close"
                            :class="{ 'text-white opacity-100': isBundle, 'text-black': !isBundle }"
                            @click="close"
                        >
                            <span class="fal fa-fw fa-times"></span>
                            <span class="sr-only">Close Modal</span>
                        </button>
                    </div>
                </div>

                <!-- Item Name -->
                <div class="px-3 pt-1 text-lg roboto">
                    {{ itemName }}
                    <small v-if="isBundle">in {{ parentName }}</small>
                </div>

                <!-- Carousel -->
                <div class="jgile-carousel">
                    <div class="modal-body">
                        <!-- Counter -->
                        <div class="counter">
                            {{ currentPage }} of {{ total }} Total Creative<br>
                            <run-dates :start="startDate" :end="endDate" />
                        </div>

                        <!-- Carousel Navigation -->
                        <div class="row switcher">
                            <!-- Previous Button -->
                            <div v-if="total > 1" class="col-2">
                                <button class="btn prev" @click="previous" :disabled="loading">
                                    <i class="fal fa-chevron-left fa-fw"></i>
                                    <span class="sr-only">Previous Creative</span>
                                </button>
                            </div>

                            <!-- Creative Display -->
                            <div :class="total > 1 ? 'col-8' : 'col-12'">
                                <div class="current-item">
                                    <div class="current-item-content">
                                        <div v-if="loading" class="text-center py-4">
                                            <i class="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <template v-else>
                                            <creative-display :creative="currentCreative" />

                                            <!-- Creative Requirements -->
                                            <div class="text-left mt-3">
                                                <label>Creative Requirements</label>
                                                <div v-if="artworkUrl" class="d-block">
                                                    <a
                                                        class="btn btn-link btn-sm mb-2"
                                                        :href="artworkUrl"
                                                        target="_blank"
                                                    >
                                                        Download Creative Document
                                                    </a>
                                                </div>
                                                <div
                                                    v-if="artworkText"
                                                    class="shadow-sm scroll-text text-left w-100"
                                                >
                                                    {{ artworkText }}
                                                </div>
                                            </div>

                                            <!-- Media Metadata -->
                                            <p v-if="currentCreative.media" class="mt-2">
                                                <span v-if="currentCreative.media.name" class="badge badge-light">
                                                    Name: {{ currentCreative.media.name }}
                                                </span>
                                                <span v-if="currentCreative.media.human_readable_size" class="badge badge-light">
                                                    Size: {{ currentCreative.media.human_readable_size }}
                                                </span>
                                                <template v-if="currentCreative.creative_type === 'image'">
                                                    <span v-if="currentCreative.media.dimensions" class="badge badge-light">
                                                        Dimensions: {{ currentCreative.media.dimensions }}
                                                    </span>
                                                    <br>
                                                    <span class="badge badge-light">
                                                        Required Dimensions: {{ currentCreative.spec_width }}x{{ currentCreative.spec_height }}px
                                                    </span>
                                                </template>
                                            </p>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <!-- Next Button -->
                            <div v-if="total > 1" class="col-2">
                                <button class="btn next" @click="next" :disabled="loading">
                                    <i class="fal fa-chevron-right fa-fw"></i>
                                    <span class="sr-only">Next Creative</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Footer: Close Button Only -->
                    <div class="modal-footer d-flex justify-content-center">
                        <button
                            class="btn btn-secondary"
                            @click="close"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import CreativeTableApi from '@js/services/CreativeTableApi';

export default {
    name: 'ViewCreativeCarousel',

    props: {
        /**
         * Array of creative IDs to view.
         */
        creativeIds: {
            type: Array,
            required: true,
        },

        /**
         * Order ID for context.
         */
        orderId: {
            type: [Number, String],
            required: true,
        },

        /**
         * Initial page to display.
         */
        initialPage: {
            type: Number,
            default: 1,
        },
    },

    data() {
        return {
            creativeItems: [],
            currentPage: this.initialPage,
            loading: false,
        };
    },

    computed: {
        /**
         * Total number of creative items.
         */
        total() {
            return this.creativeItems.length;
        },

        /**
         * Current creative being displayed.
         */
        currentCreative() {
            if (this.total === 0) return null;
            const index = this.currentPage - 1;
            return this.creativeItems[index] || null;
        },

        /**
         * Whether current creative is part of a bundle.
         */
        isBundle() {
            if (!this.currentCreative || !this.currentCreative.model) return false;
            return this.currentCreative.model.parent_id != null;
        },

        /**
         * Item name to display.
         */
        itemName() {
            if (!this.currentCreative) return '';
            if (this.currentCreative.model && this.currentCreative.model.name) {
                return this.currentCreative.model.name;
            }
            return this.currentCreative.name || '';
        },

        /**
         * Parent item name for bundles.
         */
        parentName() {
            if (!this.currentCreative || !this.currentCreative.parent_order_item) return '';
            return this.currentCreative.parent_order_item.name || '';
        },

        /**
         * Start date for run dates display.
         */
        startDate() {
            if (!this.currentCreative) return null;
            if (this.currentCreative.start_date) return this.currentCreative.start_date;
            if (this.currentCreative.model && this.currentCreative.model.start_date) {
                return this.currentCreative.model.start_date;
            }
            return null;
        },

        /**
         * End date for run dates display.
         */
        endDate() {
            if (!this.currentCreative) return null;
            if (this.currentCreative.end_date) return this.currentCreative.end_date;
            if (this.currentCreative.model && this.currentCreative.model.end_date) {
                return this.currentCreative.model.end_date;
            }
            return null;
        },

        /**
         * Artwork document URL.
         */
        artworkUrl() {
            if (!this.currentCreative || !this.currentCreative.asset) return null;
            return this.currentCreative.asset.artwork_url || null;
        },

        /**
         * Artwork text/requirements.
         */
        artworkText() {
            if (!this.currentCreative || !this.currentCreative.asset) return null;
            return this.currentCreative.asset.artwork_text || null;
        },
    },

    watch: {
        creativeIds: {
            handler() {
                this.loadCreatives();
            },
            immediate: true,
        },
    },

    methods: {
        /**
         * Load creative items from API.
         */
        async loadCreatives() {
            if (this.creativeIds.length === 0) return;

            this.loading = true;
            try {
                const response = await CreativeTableApi.getByIds(this.creativeIds);
                this.creativeItems = response.data.data || response.data;

                // Reset page if out of bounds
                if (this.currentPage > this.total) {
                    this.currentPage = 1;
                }
            } catch (error) {
                Bus.$emit('toast', 'Failed to load creative items.', 'error');
            } finally {
                this.loading = false;
            }
        },

        /**
         * Go to previous creative.
         */
        previous() {
            if (this.currentPage === 1) {
                this.currentPage = this.total;
            } else {
                this.currentPage--;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Go to next creative.
         */
        next() {
            if (this.currentPage === this.total) {
                this.currentPage = 1;
            } else {
                this.currentPage++;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Close the carousel modal.
         */
        close() {
            this.$emit('close');
            Bus.$emit('closeViewCreative');
        },
    },
};
</script>

<style scoped>
.view-creative-carousel {
    width: 100%;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
}

.modal-title {
    margin: 0;
    font-size: 1.25rem;
}

.modal-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-close {
    background: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.25rem;
}

.modal-body {
    padding: 1rem;
}

.counter {
    text-align: center;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    color: #666;
}

.switcher {
    align-items: center;
    min-height: 200px;
}

.current-item {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 180px;
}

.current-item-content {
    width: 100%;
    text-align: center;
}

.btn.prev,
.btn.next {
    padding: 10px 15px;
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
}

.btn.prev:hover:not(:disabled),
.btn.next:hover:not(:disabled) {
    background-color: #f5f5f5;
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    border-top: 1px solid #dee2e6;
}

.scroll-text {
    max-height: 150px;
    overflow-y: auto;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 4px;
}

.badge-light {
    background-color: #f8f9fa;
    color: #333;
    padding: 0.25rem 0.5rem;
    margin-right: 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
}

.modal-content-bundle .modal-header {
    background-color: #343a40;
    color: white;
}
</style>